import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { bumpVersionButton_BumpSubscriptionVersionMutation } from "@relay/bumpVersionButton_BumpSubscriptionVersionMutation.graphql";
import { BUMP_SUBSCRIPTION_VERSION_MUTATION } from "./bump-version-button.graphql";
import { BumpVersionButtonProps } from "./bump-version-button.types";

export const BumpVersionButton = ({ offerId, latestVersion, onBumped }: BumpVersionButtonProps) => {
	const { t } = useTranslation("common");
	const toast = useRef<Toast>(null);
	const [confirmVisible, setConfirmVisible] = useState(false);

	const [bumpSubscriptionVersion, isBumping] =
		useMutation<bumpVersionButton_BumpSubscriptionVersionMutation>(
			BUMP_SUBSCRIPTION_VERSION_MUTATION,
		);

	const handleConfirm = () => {
		bumpSubscriptionVersion({
			variables: { offerId, toVersion: latestVersion },
			onCompleted: (data) => {
				setConfirmVisible(false);
				const result = data.Admin.Offer.bumpSubscriptionVersion;
				if (result.kind === "OK") {
					toast.current?.show({
						severity: "success",
						life: 3000,
						summary: t("offerSubscriptions.bump.success"),
						detail: t("offerSubscriptions.bump.successDetail", {
							version: latestVersion,
						}),
					});
					onBumped();
				} else {
					toast.current?.show({
						severity: "error",
						life: 5000,
						summary: t("offerSubscriptions.bump.blocked"),
						detail: (result.missingElementTypes ?? []).join(", "),
					});
				}
			},
			onError: () => {
				setConfirmVisible(false);
				toast.current?.show({
					severity: "error",
					life: 3000,
					summary: t("offerSubscriptions.bump.error"),
					detail: t("offerSubscriptions.bump.errorDetail"),
				});
			},
		});
	};

	return (
		<>
			<Toast ref={toast} />
			<Button
				label={t("offerSubscriptions.bump.button", { version: latestVersion })}
				className="p-button-sm p-button-warning"
				onClick={() => setConfirmVisible(true)}
				disabled={isBumping}
			/>
			<Dialog
				header={t("offerSubscriptions.bump.confirmTitle", { version: latestVersion })}
				visible={confirmVisible}
				onHide={() => setConfirmVisible(false)}
				footer={
					<div className="flex gap-2 justify-content-end">
						<Button
							label={t("actions.cancel")}
							className="p-button-secondary"
							onClick={() => setConfirmVisible(false)}
							disabled={isBumping}
						/>
						<Button
							label={t("offerSubscriptions.bump.confirmButton")}
							loading={isBumping}
							onClick={handleConfirm}
						/>
					</div>
				}
			>
				<p>{t("offerSubscriptions.bump.confirmMessage", { version: latestVersion })}</p>
			</Dialog>
		</>
	);
};
